import { faAnglesLeft, faAnglesRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Link } from 'react-router-dom';

import NavSubItemsToggle from '@/components/NavSubItemsToggle';
import { Tooltip } from '@/ui/Tooltip';

import './styles.css';
import type { Props } from './types';

/**
 * Rail colors are written as light-mode utilities on purpose: ui/style/dark-mode.css remaps the
 * base color classes for the `.dark` theme, so `bg-white` becomes gray-900 and `text-gray-600`
 * becomes gray-200 without us restating them. Only `hover:` needs an explicit `dark:` variant,
 * because that file redefines base classes rather than variants.
 */
const IDLE_ITEM =
  'font-semibold text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-gray-100';
/*
 * Selected reads darker and bolder than its neighbors. The `!` is load-bearing: dark-mode.css remaps
 * `bg-green-50` outside any cascade layer, which outranks a plain `dark:` utility, and its green-900
 * leaves the label short of AAA in the midnight theme.
 */
const ACTIVE_ITEM = 'bg-green-50 font-bold text-green-950 dark:bg-green-950! dark:text-green-50';

/**
 * The shared chrome for Dugout's desktop nav rails: a fixed, dark column of icon + label links
 * that can be minimized to icons only.
 *
 * Rails are `hidden lg:flex`, so nothing about the narrow layout (phones, the Chrome extension
 * panel) changes. Callers supply the links and the branding; DesktopSidebarNav and
 * PersonSidebarNav are the two current callers.
 */
const SidebarNavRail = ({
  ariaLabel,
  header,
  collapsedHeader,
  items,
  isCurrent,
  collapsed,
  onToggleCollapsed,
  widthClass,
  leftClass,
  surfaceClassName = 'bg-white',
  zIndexClass,
  footer,
  renderSubItems,
  subItemsCollapsed = false,
  onToggleSubItems
}: Props) => (
  <div
    className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col ${zIndexClass} ${leftClass} ${widthClass}`}
    data-testid={`sidebar-rail-${ariaLabel.toLowerCase()}`}>
    <div
      className={`flex min-h-0 grow flex-col border-r border-gray-200 ${
        collapsed ? 'px-2' : 'px-3'
      } ${surfaceClassName}`}>
      <div className="flex h-14 shrink-0 items-center gap-x-2">
        {collapsed ? collapsedHeader : header}
      </div>

      {/* Only the links scroll: long person nav lists must not push the collapse toggle away. */}
      <nav
        aria-label={ariaLabel}
        className="sidebar-nav-rail-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-1">
        <ul className="space-y-1">
          {items.map((item) => {
            const { label, path, icon } = item;
            const current = isCurrent(path);
            // A collapsed rail is icons only: there is nowhere to read a nested label.
            const subItems = collapsed ? null : renderSubItems?.(item, current);
            const showCaret = !!subItems && !!onToggleSubItems;

            return (
              <li key={label}>
                <div className="relative">
                  {/* Collapsed rails hide the label, so the tooltip is the only way to read it. */}
                  <Tooltip
                    content={label}
                    placement="right"
                    disabled={!collapsed}
                    triggerClassName="block">
                    <Link
                      to={path}
                      aria-current={current ? 'page' : undefined}
                      className={`group flex items-center rounded-md py-2 text-sm ${
                        collapsed ? 'justify-center px-2' : 'gap-x-3 px-3'
                      } ${showCaret ? 'pr-9' : ''} ${current ? ACTIVE_ITEM : IDLE_ITEM}`}>
                      <FontAwesomeIcon
                        aria-hidden="true"
                        className={`h-5 w-5 shrink-0 ${current ? '' : 'text-gray-600'}`}
                        icon={icon}
                      />
                      <span className={collapsed ? 'sr-only' : 'truncate'}>{label}</span>
                    </Link>
                  </Tooltip>

                  {showCaret && (
                    <NavSubItemsToggle
                      collapsed={subItemsCollapsed}
                      onToggle={onToggleSubItems}
                      className="absolute top-1/2 right-1.5 -translate-y-1/2"
                    />
                  )}
                </div>

                {!subItemsCollapsed && subItems}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Only the workspace rail carries a footer and the toggle: one control collapses both. */}
      {(footer || onToggleCollapsed) && (
        <div className="shrink-0 space-y-1 border-t border-gray-200 pt-2 pb-2">
          {footer}

          {onToggleCollapsed && (
            <Tooltip
              content="Expand navigation"
              placement="right"
              disabled={!collapsed}
              triggerClassName="block">
              <button
                type="button"
                onClick={onToggleCollapsed}
                aria-expanded={!collapsed}
                className={`flex w-full items-center rounded-md py-2 text-sm ${IDLE_ITEM} ${
                  collapsed ? 'justify-center px-2' : 'gap-x-3 px-3'
                }`}>
                <FontAwesomeIcon
                  aria-hidden="true"
                  className="h-5 w-5 shrink-0"
                  icon={collapsed ? faAnglesRight : faAnglesLeft}
                />
                <span className={collapsed ? 'sr-only' : 'truncate'}>
                  {collapsed ? 'Expand navigation' : 'Collapse'}
                </span>
              </button>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  </div>
);

export default React.memo(SidebarNavRail);
